/**
 * Centrale error-logger — STAP 1 van het logging-systeem (Fase 2).
 *
 * Standalone singleton (géén Zustand, D1) met een eigen localStorage-sleutel
 * los van `sass-rooster-v4` (D2), zodat het foutlog nooit met de roosterconfig
 * vermengt of bij een reset wordt weggegooid.
 *
 * Eén import (lib/tijd.js, voor de bestandsstempel), verder alleen browser-API's;
 * tijd.js heeft zelf geen dependencies en draait op kale Intl. De logger faalt nooit
 * hard — een fout in de app mag niet door de logger verergerd worden.
 *
 * NB: de globale handlers (window-error / unhandledrejection) en de React
 * ErrorBoundary worden bewust pas in STAP 2 aangesloten.
 */

import { bestandsstempelNL } from './tijd.js';

// ── Constants ────────────────────────────────────────────────────────────────
const STORAGE_KEY = 'sass-rooster-logs-v1'; // D2 — los van sass-rooster-v4
const MAX_ENTRIES = 200;                     // ring-buffer cap (oudste eruit)
const MAX_STACK_CHARS = 4000;                // stacktrace-truncatie → beschermt quota

// ── Interne state ────────────────────────────────────────────────────────────
// `entries` wordt immutable behandeld: elke wijziging vervangt de array door een
// nieuwe referentie, zodat useSyncExternalStore (de hook in Actie 2) de wijziging
// ziet en hertekent.
let entries = loadFromStorage();
const listeners = new Set();
let isLogging = false; // anti-lus: een fout in de logger logt zichzelf niet

// ── localStorage (faalt nooit hard) ──────────────────────────────────────────
function hasStorage() {
  try {
    return typeof localStorage !== 'undefined';
  } catch {
    return false;
  }
}

function loadFromStorage() {
  if (!hasStorage()) return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveToStorage() {
  if (!hasStorage()) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Vermoedelijk QuotaExceeded → oudste helft droppen en één keer opnieuw.
    try {
      entries = entries.slice(Math.floor(entries.length / 2));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // Opgeven; de in-memory buffer blijft gewoon werken.
    }
  }
}

// ── Pub/sub ───────────────────────────────────────────────────────────────────
function notify() {
  for (const listener of listeners) listener();
}

/** Abonneer op wijzigingen. Retourneert een unsubscribe-functie. */
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Huidige entries (chronologisch, oudste eerst). Stabiele referentie tussen
 * wijzigingen → veilig als getSnapshot voor useSyncExternalStore.
 */
export function getEntries() {
  return entries;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function genId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // val terug op timestamp + random
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function truncate(str, max) {
  if (typeof str !== 'string') return null;
  return str.length > max ? `${str.slice(0, max)}\n…(ingekort)` : str;
}

/** Haalt message + (getrunceerde) stack uit wat dan ook dat gegooid kan worden. */
function normalizeError(error) {
  if (error instanceof Error) {
    return {
      message: error.message || String(error),
      stack: truncate(error.stack, MAX_STACK_CHARS),
    };
  }
  if (typeof error === 'string') return { message: error, stack: null };
  if (error == null) return { message: 'Onbekende fout (geen details)', stack: null };
  try {
    return { message: JSON.stringify(error), stack: null };
  } catch {
    return { message: String(error), stack: null };
  }
}

function makeRecord(level, source, message, stack, context) {
  return {
    id: genId(),
    timestamp: new Date().toISOString(),
    level,
    source,
    message,
    stack,
    context: context && Object.keys(context).length ? context : null,
    url: typeof location !== 'undefined' ? location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    count: 1,
  };
}

/** Kern: voegt een record toe (met ontdubbeling + ring-buffer-trim). */
function push(level, source, message, stack, context) {
  const last = entries[entries.length - 1];
  if (last && last.message === message && last.source === source && last.stack === stack) {
    // Identiek aan de vorige regel → ophogen i.p.v. een nieuwe regel.
    const bumped = { ...last, count: last.count + 1, timestamp: new Date().toISOString() };
    entries = [...entries.slice(0, -1), bumped];
  } else {
    entries = [...entries, makeRecord(level, source, message, stack, context)];
    if (entries.length > MAX_ENTRIES) {
      entries = entries.slice(entries.length - MAX_ENTRIES);
    }
  }
  saveToStorage();
  notify();
}

// ── Publieke API ──────────────────────────────────────────────────────────────
/**
 * Log een fout. `context` mag een `source` bevatten ('engine', 'react', …);
 * de overige velden worden als context bewaard.
 *   logError(err, { source: 'engine', weeknummer, jaar })
 */
export function logError(error, context = {}) {
  if (isLogging) return;
  isLogging = true;
  try {
    const { source = 'manual', ...meta } = context || {};
    const { message, stack } = normalizeError(error);
    push('error', source, message, stack, meta);
  } catch {
    // De logger mag de app nooit laten crashen.
  } finally {
    isLogging = false;
  }
}

/** Lichte variant voor 'info'/'warn' (zonder stacktrace). */
export function log(level = 'info', message = '', context = {}) {
  if (isLogging) return;
  isLogging = true;
  try {
    const { source = 'manual', ...meta } = context || {};
    push(level, source, String(message), null, meta);
  } catch {
    // stil
  } finally {
    isLogging = false;
  }
}

/** Leegt de buffer én localStorage. */
export function clear() {
  entries = [];
  saveToStorage();
  notify();
}

// ── Export naar bestand ─────────────────────────────────────────────────────────
function formatEntryText(e) {
  const head = `[${e.timestamp}] ${e.level.toUpperCase()} (${e.source})${e.count > 1 ? ` ×${e.count}` : ''}`;
  const ctx = e.context ? `\ncontext: ${JSON.stringify(e.context)}` : '';
  const stack = e.stack ? `\n${e.stack}` : '';
  return `${head}\n${e.message}${ctx}${stack}`;
}

/**
 * Downloadt het log als bestand. `json` = volledige records (machine-leesbaar),
 * `txt` = leesbare blokken. Bestandsnaam: sass-rooster-log-<timestamp>.<ext>
 */
export function exportAsFile(format = 'json') {
  if (typeof document === 'undefined') return;

  let content, mime, ext;
  if (format === 'txt') {
    content = entries.map(formatEntryText).join('\n\n');
    mime = 'text/plain';
    ext = 'log';
  } else {
    content = JSON.stringify(entries, null, 2);
    mime = 'application/json';
    ext = 'json';
  }

  // bestandsstempelNL, niet toISOString: die noemt een export van 00:05 naar gisteren
  // en zet er het UTC-uur bij — zie lib/tijd.js.
  const stamp = bestandsstempelNL();
  const blob = new Blob([content], { type: mime });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `sass-rooster-log-${stamp}.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

// ── Globale handlers (STAP 2) ────────────────────────────────────────────────
let handlersInstalled = false;

/**
 * Koppelt de globale browser-handlers aan de logger. Idempotent: een tweede
 * aanroep (bv. via React StrictMode) doet niets. Vangt onafgevangen runtime-
 * fouten en niet-afgehandelde promise-rejections. (Resource-laadfouten worden
 * bewust niet gevangen — die vereisen de capture-fase.)
 */
export function installGlobalHandlers() {
  if (handlersInstalled || typeof window === 'undefined') return;
  handlersInstalled = true;

  window.addEventListener('error', (event) => {
    logError(event.error ?? event.message, {
      source: 'window',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logError(event.reason ?? 'Onafgehandelde promise-rejection', { source: 'promise' });
  });
}
